import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { motion } from "framer-motion";
import { BadgeCheck, ChevronLeft, Loader2, Lock, Music, ShieldCheck, Sparkles, Zap, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SiteFooter } from "@/components/SiteFooter";
import { PromoCodeBox, PromoLine } from "@/components/PromoCodeBox";

const dollars = (cents) => (cents / 100).toFixed(cents % 100 ? 2 : 0);

// Stripe.js instance must be stable for the lifetime of the page — cache it
let stripePromiseCache = null;
const getStripe = (key) => {
  if (!stripePromiseCache) stripePromiseCache = loadStripe(key);
  return stripePromiseCache;
};

const PaymentForm = ({ summary, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [ready, setReady] = useState(false);

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (error) {
      toast.error(error.message || "That card didn't go through — try another one.");
      setPaying(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      onPaid();
      return;
    }
    toast.error("Payment is still processing — hang tight.");
    setPaying(false);
  };

  return (
    <form data-testid="card-form" onSubmit={pay} className="space-y-6">
      <PaymentElement
        onReady={() => setReady(true)}
        options={{
          layout: "tabs",
          // No Link wallet: its email/SMS code flow confuses buyers (and sends
          // no codes in test mode). Also kills the Link-powered "Bank" tab promo.
          wallets: { link: "never" },
        }}
      />
      <button
        data-testid="pay-button"
        type="submit"
        disabled={!stripe || !ready || paying}
        className="w-full bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold text-base sm:text-lg px-8 py-4 rounded-xl shadow-[0_0_36px_rgba(255,183,3,0.4)] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 cursor-pointer disabled:opacity-70 disabled:cursor-wait flex items-center justify-center gap-2.5"
      >
        {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
        {paying ? "Processing..." : `PAY $${dollars(summary.amount)} — UNLOCK MY JINGLE`}
      </button>
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Stripe 256-bit SSL</span>
        <span className="flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-emerald-400" /> Instant download</span>
      </div>
      <p className="text-center text-[11px] text-slate-600 leading-relaxed">
        By paying you agree we may charge this card for optional add-ons you
        explicitly approve with one click afterward. No surprises — nothing charges without your click.
      </p>
    </form>
  );
};

export default function Checkout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("pi");
  const [stripePromise, setStripePromise] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError(true);
      return;
    }
    (async () => {
      try {
        const [{ data: cfg }, { data: intent }] = await Promise.all([
          api.get("/payments/config"),
          api.get(`/payments/intent/${sessionId}`),
        ]);
        if (intent.already_paid) {
          navigate(`/payment/success?session_id=${encodeURIComponent(sessionId)}&site_key=${intent.site_key || ""}`, { replace: true });
          return;
        }
        setStripePromise(getStripe(cfg.publishable_key));
        setSummary(intent);
      } catch (e) {
        setError(true);
      }
    })();
  }, [sessionId]);

  const extras = summary?.extra_takes || [];
  const credits = summary?.credit_applied_cents || 0;

  const elementsOptions = useMemo(() => summary && ({
    clientSecret: summary.client_secret,
    appearance: {
      theme: "night",
      variables: {
        colorPrimary: "#FFB703",
        colorBackground: "#101014",
        colorText: "#F5F5F7",
        colorDanger: "#FF0055",
        fontFamily: "system-ui, sans-serif",
        borderRadius: "12px",
      },
    },
  }), [summary]);

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-obsidian text-white flex flex-col">
      <header className="mx-auto w-full max-w-5xl px-4 sm:px-8 pt-10 flex items-center justify-between">
        <Link to="/" data-testid="checkout-logo" className="font-display font-extrabold tracking-tight">
          <span className="text-gold">Local</span>Business<span className="text-gold">Jingle</span><span className="text-slate-500">.com</span>
        </Link>
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">
          <Lock className="h-3.5 w-3.5" /> Secure checkout
        </span>
      </header>

      <main className="mx-auto w-full max-w-5xl px-4 sm:px-8 pt-10 pb-20 flex-1">
        {error && (
          <div data-testid="checkout-error" className="text-center pt-16">
            <p className="font-display text-2xl font-bold mb-4">This checkout link has gone cold.</p>
            <Link to="/" className="text-gold underline">Cut a new jingle</Link>
          </div>
        )}

        {!error && !summary && (
          <div data-testid="checkout-loading" className="text-center pt-16">
            <Loader2 className="h-10 w-10 text-gold animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Setting up your secure checkout...</p>
          </div>
        )}

        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start"
          >
            {/* Order summary */}
            <section data-testid="checkout-summary" className="glass-panel rounded-3xl p-6 sm:p-8 order-2 lg:order-1">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">Your order</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-6">
                {summary.business_name ? <><span className="text-neon">{summary.business_name}</span> — the master package.</> : "Your master package."}
              </h1>
              <ul className="space-y-2.5 mb-6 text-sm text-slate-300">
                <li className="flex items-start gap-2.5"><Music className="h-4 w-4 text-gold mt-0.5 shrink-0" /> Main Jingle + Short Hook — clean masters, no watermark</li>
                <li className="flex items-start gap-2.5"><ScrollText className="h-4 w-4 text-gold mt-0.5 shrink-0" /> Unlimited lifetime commercial rights</li>
                {summary.order_bump && (
                  <li className="flex items-start gap-2.5"><Zap className="h-4 w-4 text-coral mt-0.5 shrink-0" /> Custom Instrumental Hold Music</li>
                )}
                {extras.length > 0 && (
                  <li className="flex items-start gap-2.5"><Sparkles className="h-4 w-4 text-neon mt-0.5 shrink-0" />
                    Bonus take{extras.length === 1 ? "" : "s"}: {extras.map((t) => `Take ${t.batch_index * 3 + t.take_index + 1}`).join(", ")} — clean, no watermark
                  </li>
                )}
              </ul>
              <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-400">
                  <span>Lifetime Jingle Package</span><span>$197</span>
                </div>
                {credits > 0 && (
                  <div data-testid="checkout-credits-line" className="flex justify-between text-emerald-400">
                    <span>Preview credits</span><span>−${dollars(credits)}</span>
                  </div>
                )}
                <PromoLine coupon={summary.coupon} />
                {summary.order_bump && (
                  <div data-testid="checkout-bump-line" className="flex justify-between text-slate-400">
                    <span>Instrumental Hold Music</span><span>$47</span>
                  </div>
                )}
                {extras.length > 0 && (
                  <div data-testid="checkout-extras-line" className="flex justify-between text-slate-400">
                    <span>Bonus take{extras.length === 1 ? "" : "s"} × {extras.length}</span><span>${extras.length * 17}</span>
                  </div>
                )}
                <div className="flex justify-between font-display font-extrabold text-xl pt-2 border-t border-white/10">
                  <span>Total</span>
                  <span data-testid="checkout-total" className="text-gold">${dollars(summary.amount)}</span>
                </div>
              </div>
              <PromoCodeBox
                sessionId={sessionId}
                coupon={summary.coupon}
                onUpdate={setSummary}
                onFreeUnlock={(d) => navigate(`/payment/success?session_id=${encodeURIComponent(sessionId)}&site_key=${d.site_key || ""}`)}
              />
              {summary.job_id && (
                <Link
                  data-testid="back-to-preview"
                  to={`/jingle-preview/${summary.job_id}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-gold transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" /> Back to the takes
                </Link>
              )}
            </section>

            {/* Card form */}
            <section className="rounded-3xl border border-gold/40 bg-ink p-6 sm:p-8 shadow-[0_0_60px_rgba(255,183,3,0.12)] order-1 lg:order-2">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">Payment</p>
              {/* key: a promo changes the PaymentIntent amount — remount so Elements re-reads it */}
              <Elements key={summary.amount} stripe={stripePromise} options={elementsOptions}>
                <PaymentForm
                  summary={summary}
                  onPaid={() => navigate(`/payment/success?session_id=${encodeURIComponent(sessionId)}&site_key=${summary.site_key || ""}`)}
                />
              </Elements>
            </section>
          </motion.div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
